import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  activateAccount,
  resendActivationCode,
  ApiError,
} from '../../api/client';
import './ActivateForm.css';

const CODE_LENGTH = 6; // backend app.otp.length
const RESEND_SECONDS = 42; // matches the "00:42" cooldown in the design

function formatTimer(seconds) {
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * Account-activation step (step 2). Collects the 6-digit OTP that the backend
 * emailed during registration and confirms it via POST /users/activate.
 */
function ActivateForm({ email, onActivated, onChangeEmail }) {
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);
  const inputsRef = useRef([]);

  const code = useMemo(() => digits.join(''), [digits]);
  const complete = code.length === CODE_LENGTH && !digits.includes('');

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const setDigit = (index, value) => {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleChange = (index) => (event) => {
    const raw = event.target.value.replace(/\D/g, '');
    if (error) setError('');

    if (raw.length > 1) {
      // Handles typing fast or pasting a few digits into one box.
      const chars = raw.slice(0, CODE_LENGTH - index).split('');
      setDigits((prev) => {
        const next = [...prev];
        chars.forEach((char, i) => {
          next[index + i] = char;
        });
        return next;
      });
      const nextFocus = Math.min(index + chars.length, CODE_LENGTH - 1);
      inputsRef.current[nextFocus]?.focus();
      return;
    }

    setDigit(index, raw);
    if (raw && index < CODE_LENGTH - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index) => (event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
      setDigit(index - 1, '');
    }
    if (event.key === 'ArrowLeft' && index > 0) inputsRef.current[index - 1]?.focus();
    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1)
      inputsRef.current[index + 1]?.focus();
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    event.preventDefault();
    const chars = pasted.slice(0, CODE_LENGTH).split('');
    const next = Array(CODE_LENGTH).fill('');
    chars.forEach((char, i) => {
      next[i] = char;
    });
    setDigits(next);
    inputsRef.current[Math.min(chars.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    if (!complete) {
      setError('Introduce los 6 dígitos del código.');
      return;
    }

    setSubmitting(true);
    try {
      await activateAccount({ email, code });
      onActivated();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail || err.message);
      } else {
        setError('No hemos podido verificar el código. Inténtalo de nuevo.');
      }
      setDigits(Array(CODE_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError('');
    setNotice('');
    try {
      await resendActivationCode({ email });
      setNotice('Te hemos enviado un código nuevo.');
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail || err.message
          : 'No hemos podido reenviar el código.'
      );
    }
  };

  return (
    <form className="AuthForm-fields" onSubmit={handleSubmit} noValidate>
      <h2 className="AuthForm-heading">Introduce tu código</h2>
      <p className="AuthForm-sub">
        Hemos enviado seis dígitos a <strong>{email}</strong>. Caduca en diez
        minutos.
      </p>

      <span className="AuthLabel">Código de verificación</span>
      <div className="Activate-otp" onPaste={handlePaste}>
        {digits.map((digit, index) => (
          <input
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            ref={(el) => {
              inputsRef.current[index] = el;
            }}
            className={`Activate-cell${digit ? '' : ' Activate-cell--empty'}${
              error ? ' Activate-cell--invalid' : ''
            }`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            aria-label={`Dígito ${index + 1}`}
            value={digit}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
          />
        ))}
      </div>

      {error && <p className="AuthBanner">{error}</p>}
      {notice && <p className="Activate-notice">{notice}</p>}

      <div className="Activate-resend">
        <span className="Activate-resendText">
          Puedes pedir otro código{' '}
          {cooldown > 0 ? (
            <>en <strong>{formatTimer(cooldown)}</strong></>
          ) : (
            'ahora'
          )}
          .
        </span>
        <button
          type="button"
          className="Activate-resendBtn"
          onClick={handleResend}
          disabled={cooldown > 0}
        >
          Reenviar
        </button>
      </div>

      <svg
        className="Activate-elevation"
        viewBox="0 0 900 60"
        width="100%"
        height="60"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M0 48 L150 52 L300 22 L450 36 L600 10 L750 34 L900 18"
          stroke="var(--flik-border-cream)"
          strokeWidth="2"
          strokeLinecap="butt"
        />
        <path
          d="M600 10 L600 60"
          stroke="var(--flik-border-cream)"
          strokeWidth="1"
          strokeDasharray="3 5"
        />
      </svg>

      <div className="Activate-actions">
        <button
          className="AuthSubmit"
          type="submit"
          disabled={submitting || !complete}
        >
          {submitting ? 'Activando…' : 'Activar cuenta →'}
        </button>
        <button type="button" className="AuthLink" onClick={onChangeEmail}>
          Cambiar correo
        </button>
        {
          process.env.REACT_APP_SMS_ENABLED === 'true' && (
            <button
          type="button"
          className="AuthLink"
          onClick={() => setNotice('La verificación por SMS llegará pronto.')}
        >
          Verificar por SMS
        </button>
          )
        }
      </div>
    </form>
  );
}

export default ActivateForm;
